/*
 * Opens the Allied engraving builder for the currently selected variant and carries the
 * saved engraving through to the cart as line item properties.
 *
 * Allied's builder takes a single metal colour ('red', 'yellow' or 'white') and a width in
 * millimetres, so the Metal option value is reduced to one of those three colours and the
 * Width option value to a number. Two tone metals ("9ct White Gold with 9ct Yellow Gold
 * Sleeve") are represented by their base metal, the part before "with".
 */
if (!customElements.get('engraving-builder')) {
  const MIN_WIDTH = 2;
  const MAX_WIDTH = 8;
  const DEFAULT_FONT = 'Love Letter';

  /* Allied's builder is mounted once and shared by every trigger on the page. */
  let builderPromise = null;

  /*
   * The builder's overlay is position: fixed, but the product info column is sticky and the
   * animation wrapper around it is transformed, either of which makes a fixed descendant
   * resolve against that ancestor instead of the viewport. Mounting from the body keeps the
   * modal full screen and centred.
   */
  function mountPoint() {
    const element = document.getElementById('engravingApp');
    if (element && element.parentElement !== document.body) document.body.appendChild(element);

    return element;
  }

  function mountBuilder() {
    if (builderPromise) return builderPromise;

    builderPromise = bundleLoaded().then(() => {
      if (typeof window.EngravingPreview === 'undefined') return null;

      mountPoint();
      return window.EngravingPreview.mount('#engravingApp');
    });

    return builderPromise;
  }

  /*
   * The builder holds the last engraving in its own state, so a removed engraving would
   * reappear the next time the modal opened. setText clears both the working and the saved
   * value and setFont returns the picker to its default. Neither is in Allied's guide, so fall
   * back to remounting if a future build drops them.
   */
  function resetBuilder(builder) {
    if (typeof builder.setText === 'function' && typeof builder.setFont === 'function') {
      builder.setText('');
      builder.setFont(DEFAULT_FONT);

      return Promise.resolve(builder);
    }

    return remountBuilder();
  }

  function remountBuilder() {
    const element = mountPoint();
    if (element) element.innerHTML = '';
    builderPromise = null;

    return mountBuilder();
  }

  /*
   * Allied's panel is destroyed when the modal closes, taking our close cross with it, so
   * watch the mount point and add the cross back whenever a panel appears without one.
   */
  function watchForPanel(addCross) {
    const mount = mountPoint();
    if (!mount || mount.dataset.engravingCrossWatched) return;

    mount.dataset.engravingCrossWatched = 'true';
    new MutationObserver(() => {
      const panel = mount.querySelector('.engraving-preview > div');
      if (panel && !panel.querySelector('[data-engraving-close]')) addCross(panel);
    }).observe(mount, { childList: true, subtree: true });
  }

  function bundleLoaded() {
    if (typeof window.EngravingPreview !== 'undefined') return Promise.resolve();

    const bundle = document.getElementById('engraving-preview-bundle');
    if (!bundle) return Promise.resolve();

    return new Promise((resolve) => {
      bundle.addEventListener('load', resolve, { once: true });
      bundle.addEventListener('error', resolve, { once: true });
    });
  }

  customElements.define(
    'engraving-builder',
    class EngravingBuilder extends HTMLElement {
      constructor() {
        super();

        this.trigger = this.querySelector('[data-engraving-trigger]');
        this.summary = this.querySelector('[data-engraving-summary]');
        this.savedText = this.querySelector('[data-engraving-saved-text]');
        this.savedFont = this.querySelector('[data-engraving-saved-font]');
        this.removeButton = this.querySelector('[data-engraving-remove]');
        this.textInput = this.querySelector('[data-engraving-text]');
        this.fontInput = this.querySelector('[data-engraving-font]');
      }

      connectedCallback() {
        if (!this.trigger) return;

        this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
        if (this.removeButton) {
          this.removeButton.addEventListener('click', this.clearEngraving.bind(this));
        }
      }

      onTriggerClick() {
        const needsReset = this.builder && this.textInput && this.textInput.value === '';

        mountBuilder()
          .then((builder) => (builder && needsReset ? resetBuilder(builder) : builder))
          .then((builder) => {
            if (!builder) return;

            if (this.builder !== builder) {
              builder.onSelected(this.onSelected.bind(this));
              builder.onRemoved(this.clearEngraving.bind(this));
              this.builder = builder;
              watchForPanel(this.addCloseCross.bind(this));
            }

            builder.openModal(this.metalColour(), this.ringWidth());
          });
      }

      onSelected(selection) {
        const text = (selection && selection.engravingText ? selection.engravingText : '').trim();
        if (!text) {
          this.clearEngraving();
          return;
        }

        const font = (selection && selection.font) || '';

        this.setInput(this.textInput, text);
        this.setInput(this.fontInput, font);

        if (this.savedText) this.savedText.textContent = text;
        if (this.savedFont) this.savedFont.textContent = font;
        if (this.summary) this.summary.hidden = false;
        this.trigger.textContent = this.dataset.editLabel;
      }

      clearEngraving() {
        this.setInput(this.textInput, '');
        this.setInput(this.fontInput, '');

        /* Emptied as well as hidden, so a removed engraving can never be left on show. */
        if (this.savedText) this.savedText.textContent = '';
        if (this.savedFont) this.savedFont.textContent = '';
        if (this.summary) this.summary.hidden = true;
        this.trigger.textContent = this.dataset.triggerLabel;
      }

      addCloseCross(panel) {
        const cross = document.createElement('button');
        cross.type = 'button';
        cross.className = 'engraving-builder__close';
        cross.setAttribute('data-engraving-close', '');
        cross.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
        cross.innerHTML =
          '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">' +
          '<path d="M1 1 13 13M13 1 1 13" stroke="currentColor" stroke-width="1.5" fill="none"/>' +
          '</svg>';
        cross.addEventListener('click', this.closeModal.bind(this));

        panel.insertBefore(cross, panel.firstChild);
      }

      /*
       * Allied's own Close button is what fires their cancelled callback, so use it when it is
       * there and keep close() as the fallback.
       */
      closeModal() {
        const mount = document.getElementById('engravingApp');
        const theirClose = mount
          ? Array.prototype.find.call(
              mount.querySelectorAll('button:not([data-engraving-close])'),
              (button) => button.textContent.trim().toLowerCase() === 'close'
            )
          : null;

        if (theirClose) {
          theirClose.click();
          return;
        }

        if (this.builder && typeof this.builder.close === 'function') this.builder.close();
      }

      /* Empty inputs stay disabled so Shopify never receives a blank property. */
      setInput(input, value) {
        if (!input) return;

        input.value = value;
        input.disabled = value === '';
      }

      metalColour() {
        const metal = this.selectedOption(this.dataset.metalOption);
        if (!metal) return 'white';

        const baseMetal = metal.split(/\swith\s/i)[0].toLowerCase();
        if (baseMetal.indexOf('rose') > -1 || baseMetal.indexOf('red') > -1) return 'red';
        if (baseMetal.indexOf('yellow') > -1) return 'yellow';
        return 'white';
      }

      ringWidth() {
        const width = parseFloat(this.selectedOption(this.dataset.widthOption));
        const fallback = parseFloat(this.dataset.defaultWidth);
        if (isNaN(width)) return isNaN(fallback) ? MIN_WIDTH : fallback;

        return Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);
      }

      /* Variant picker radios are tied to the product form, so scope the lookup to it. */
      selectedOption(name) {
        if (!name) return '';

        const selected = document.querySelector(
          `input[type="radio"][form="${this.dataset.formId}"][name="${name}"]:checked`
        );
        return selected ? selected.value : '';
      }
    }
  );
}
